import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tab,
    Tabs,
    TextField,
    Theme,
    Tooltip,
    Typography,
    MenuItem,
} from '@material-ui/core'
import { toast } from 'react-toastify'
import VisibilityIcon from '@material-ui/icons/Visibility'
import EditIcon from '@material-ui/icons/Edit'
import ShareIcon from '@material-ui/icons/Share'
import DeleteIcon from '@material-ui/icons/Delete'
import RefreshIcon from '@material-ui/icons/Refresh'
import DescriptionIcon from '@material-ui/icons/Description'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import ErrorIcon from '@material-ui/icons/Error'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import { useOrgCompanyContracts } from '../../../../hooks/useOrgCompanyContracts'
import { updateContractRequest } from '../../../../hooks/useCompanyContracts'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { useOrganizationManagement } from '../../../../hooks/useOrganizationManagement'
import {
    ApartmentContract,
    ApartmentContractType,
    Company,
    ContractLifecycleStatus,
} from '../../../../models/Company/types'
import { ContractViewModal } from '../../../ContractManager/ContractViewModal'
import { formatDate } from '../../../ContractManager/utils'
import { DeleteConfirmDialog } from '../../../ContractManager/DeleteConfirmDialog'
import { DropFiles } from '../../../../components/DropFiles/DropFiles'
import { SelectPropertiesUploadDialog } from './SelectPropertiesUploadDialog'
import { CatalogWorkspaceHeader } from './CatalogWorkspaceHeader'
import { OrganizationContractTypesModal } from './OrganizationContractTypesModal'
import { OrganizationContractTypeCoverage } from './OrganizationContractTypeCoverage'
import {
    ContractStatusChip,
    lifecycleRowClassName,
    lifecycleRowShadeStyles,
} from '../../../ContractManager/ContractStatusChip'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    FilterChipItem,
    PropertyTagFilterField,
    getPropertyTagChip,
} from '../../../ContractManager/CatalogFilterChrome'
import { ContractAlertsPanel } from '../../../ContractManager/ContractAlertsPanel'
import { ContractAlertEmailSettingsDialog } from '../../../ContractManager/ContractAlertEmailSettingsDialog'
import { useContractAlerts } from '../../../../hooks/useContractAlerts'
import { useOrganizationCertificateTypes } from '../../../../hooks/useOrganizationCertificateTypes'
import { useOrganizationCertificateTypeCoverage } from '../../../../hooks/useOrganizationCertificateTypeCoverage'
import { useOrganizationContractTypes } from '../../../../hooks/useOrganizationContractTypes'
import { useOrganizationContractTypeCoverage } from '../../../../hooks/useOrganizationContractTypeCoverage'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    searchField: {
        minWidth: 240,
        flex: '1 1 220px',
        maxWidth: 360,
    },
    filterField: {
        minWidth: 160,
    },
    analyticsContainer: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
        width: '100%',
        minWidth: 0,
    },
    analyticsCard: {
        flex: '1 1 0',
        minWidth: 0,
        padding: theme.spacing(1.5, 1.25),
        borderRadius: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
            boxShadow: theme.shadows[3],
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 0.75),
            gap: theme.spacing(0.75),
        },
    },
    analyticsIconContainer: {
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
            width: 28,
            height: 28,
        },
    },
    analyticsIcon: {
        fontSize: 22,
        [theme.breakpoints.down('sm')]: {
            fontSize: 16,
        },
    },
    analyticsContent: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flex: 1,
    },
    analyticsValue: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.2,
        [theme.breakpoints.down('sm')]: {
            fontSize: 18,
        },
    },
    analyticsLabel: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontWeight: 500,
        lineHeight: 1.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        [theme.breakpoints.down('sm')]: {
            fontSize: 10,
        },
    },
    totalCard: {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
    totalIconContainer: {
        backgroundColor: theme.palette.primary.light + '30',
    },
    totalIcon: {
        color: theme.palette.primary.main,
    },
    activeCard: {
        borderLeft: '4px solid #22c55e',
    },
    activeIconContainer: {
        backgroundColor: '#dcfce7',
    },
    activeIcon: {
        color: '#16a34a',
    },
    endingSoonCard: {
        borderLeft: '4px solid #f59e0b',
    },
    endingSoonIconContainer: {
        backgroundColor: '#fef3c7',
    },
    endingSoonIcon: {
        color: '#d97706',
    },
    autoRenewCard: {
        borderLeft: '4px solid #f59e0b',
    },
    autoRenewIconContainer: {
        backgroundColor: '#ffedd5',
    },
    autoRenewIcon: {
        color: '#c2410c',
    },
    clickableCard: {
        cursor: 'pointer',
    },
    selectedCard: {
        boxShadow: '0 0 0 2px #2563eb',
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    expiredCard: {
        borderLeft: '4px solid #ef4444',
    },
    expiredIconContainer: {
        backgroundColor: '#fee2e2',
    },
    expiredIcon: {
        color: '#dc2626',
    },
    noDatesCard: {
        borderLeft: '4px solid #6b7280',
    },
    noDatesIconContainer: {
        backgroundColor: '#f3f4f6',
    },
    noDatesIcon: {
        color: '#6b7280',
    },
    tableWrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 500px)',
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    ...lifecycleRowShadeStyles,
    nameCell: {
        fontWeight: 500,
    },
    apartmentCell: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    propertyCell: {
        fontSize: '14px',
        maxWidth: 400,
        verticalAlign: 'middle',
    },
    propertyNamesMore: {
        fontWeight: 600,
        marginLeft: 4,
        cursor: 'default',
    },
    tagChip: {
        height: 20,
        fontSize: '11px',
        fontWeight: 500,
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
        minWidth: 300,
        maxWidth: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontStyle: 'italic',
        fontSize: '13px',
    },
    actionsCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        width: '1%',
        minWidth: 220,
    },
    emptyRow: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(6, 3),
    },
    emptyTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
    uploadSection: {
        marginBottom: theme.spacing(3),
    },
    uploadSectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    uploadSectionIcon: {
        fontSize: 20,
        color: theme.palette.primary.main,
    },
    uploadSectionLabel: {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
    },
    contractTypeChoice: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(1, 1.5),
    },
    contractTypeOption: {
        display: 'block',
        margin: 0,
        padding: theme.spacing(1, 0.5),
    },
}))

const allowedFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx',
    ],
    'text/plain': ['.txt'],
}

const CONTRACT_TYPE_LABELS: Record<ApartmentContractType, string> = {
    [ApartmentContractType.CERTIFICATE]: 'Certificates',
    [ApartmentContractType.TURN]: 'Turn Vendors',
    [ApartmentContractType.GENERAL_VENDOR]: 'General Vendor',
}

const CONTRACT_CATEGORIES: {
    value: ApartmentContractType
    label: string
}[] = [
    { value: ApartmentContractType.TURN, label: 'Turn Vendors' },
    { value: ApartmentContractType.GENERAL_VENDOR, label: 'General Vendor' },
]

interface OrganizationContractsProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
    propertyList: Company[]
    /** Certificates is its own manager tab. Contracts keeps vendor categories. */
    variant?: 'contracts' | 'certificates'
}

const formatContractType = (type?: ApartmentContractType): string => {
    if (!type) {
        return CONTRACT_TYPE_LABELS[ApartmentContractType.GENERAL_VENDOR]
    }
    return CONTRACT_TYPE_LABELS[type] || type
}

export const OrganizationContracts: React.FC<OrganizationContractsProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
    propertyList,
    variant = 'contracts',
}) => {
    const isCertificates = variant === 'certificates'
    const classes = useStyles()
    const { userTags } = useUserTags()

    const {
        contracts,
        isLoadingContracts,
        uploadContracts,
        processContract,
        updateContract,
        deleteContract,
        processingIds,
        isUploading,
        isUpdating,
        isDeleting,
    } = useOrgCompanyContracts({ organization })

    const {
        vendorList: orgVendorList,
        getOrganizationVendors,
    } = useOrganizationManagement()

    useEffect(() => {
        if (organization) {
            getOrganizationVendors(organization)
        }
    }, [organization])

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [
        pendingContractType,
        setPendingContractType,
    ] = useState<ApartmentContractType>(ApartmentContractType.GENERAL_VENDOR)

    // Search state
    const [searchText, setSearchText] = useState('')
    const [
        contractTypeFilter,
        setContractTypeFilter,
    ] = useState<ApartmentContractType>(ApartmentContractType.GENERAL_VENDOR)
    const [statusFilter, setStatusFilter] = useState<
        ContractLifecycleStatus | 'all'
    >('all')
    const [catalogTypeFilter, setCatalogTypeFilter] = useState<
        'all' | 'untyped' | number
    >('all')
    const [propertyFilter, setPropertyFilter] = useState<'all' | number>('all')
    const [vendorFilter, setVendorFilter] = useState<
        'all' | 'unassigned' | number
    >('all')
    const [autoRenewFilter, setAutoRenewFilter] = useState<
        'all' | 'yes' | 'no'
    >('all')
    const [viewMode, setViewMode] = useState<'library' | 'coverage'>('library')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [emailSettingsOpen, setEmailSettingsOpen] = useState(false)
    const [contractTypesOpen, setContractTypesOpen] = useState(false)
    const [
        pendingOrganizationContractTypeId,
        setPendingOrganizationContractTypeId,
    ] = useState<number | ''>('')
    const { alerts } = useContractAlerts({
        organization: organization ?? undefined,
        enabled: organization != null,
    })
    const { contractTypes } = useOrganizationContractTypes({
        organization: isCertificates ? null : organization,
        includeInactive: false,
    })
    const { certificateTypes } = useOrganizationCertificateTypes({
        organization: isCertificates ? organization : null,
        includeInactive: false,
    })
    const catalogTypes = isCertificates ? certificateTypes : contractTypes
    const {
        coverage: contractTypeCoverage,
        isLoading: isLoadingContractCoverage,
    } = useOrganizationContractTypeCoverage({
        organization,
        includeInactive: false,
        enabled: !isCertificates && organization != null,
    })
    const {
        coverage: certificateTypeCoverage,
        isLoading: isLoadingCertificateCoverage,
    } = useOrganizationCertificateTypeCoverage({
        organization,
        includeInactive: false,
        enabled: isCertificates && organization != null,
    })
    const typeCoverage = isCertificates
        ? certificateTypeCoverage
        : contractTypeCoverage
    const isLoadingCoverage = isCertificates
        ? isLoadingCertificateCoverage
        : isLoadingContractCoverage

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // View modal state
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [viewStartsEditing, setViewStartsEditing] = useState(false)
    const [
        selectedContract,
        setSelectedContract,
    ] = useState<ApartmentContract | null>(null)
    const viewedContract = useMemo(() => {
        if (!selectedContract) return null
        return (
            contracts.find((item) => item.id === selectedContract.id) ??
            selectedContract
        )
    }, [contracts, selectedContract])

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [
        contractToDelete,
        setContractToDelete,
    ] = useState<ApartmentContract | null>(null)

    // Reset page when tag or contract type filter changes
    useEffect(() => {
        setPage(0)
    }, [
        selectedTagId,
        contractTypeFilter,
        catalogTypeFilter,
        propertyFilter,
        vendorFilter,
        autoRenewFilter,
        statusFilter,
        viewMode,
    ])

    // Get all tags for a property (a property can have multiple tags)
    const getTagsForProperty = useCallback(
        (apartmentId: number) => {
            return userTags.filter((tag) =>
                tag.apartments.includes(apartmentId),
            )
        },
        [userTags],
    )

    const scopedContracts = useMemo(() => {
        let filtered = contracts

        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((contract) =>
                    (
                        contract.apartment_ids ??
                        contract.apartments ??
                        []
                    ).some((id) => selectedTag.apartments.includes(id)),
                )
            }
        }

        return filtered.filter((contract) => {
            const type =
                contract.contract_type ?? ApartmentContractType.GENERAL_VENDOR
            return isCertificates
                ? type === ApartmentContractType.CERTIFICATE
                : type === contractTypeFilter
        })
    }, [contracts, selectedTagId, userTags, isCertificates, contractTypeFilter])

    const scopedAnalytics = useMemo(
        () => ({
            total: scopedContracts.length,
            active: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'active',
            ).length,
            ending_soon: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'ending_soon',
            ).length,
            auto_renew_notice: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'auto_renew_notice',
            ).length,
            expired: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'expired',
            ).length,
            no_dates: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'no_dates',
            ).length,
        }),
        [scopedContracts],
    )

    const vendorOptions = useMemo(() => {
        const names = new Map<number, string>()
        scopedContracts.forEach((contract) => {
            if (contract.vendor?.id) {
                names.set(contract.vendor.id, contract.vendor.name)
            }
        })
        return Array.from(names.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [scopedContracts])

    const coverageAlertCount = useMemo(
        () =>
            typeCoverage.reduce(
                (total, group) => total + (group.required_missing_count || 0),
                0,
            ),
        [typeCoverage],
    )

    // Filter contracts based on tag, category, status, and search text
    const filteredContracts = useMemo(() => {
        let filtered = scopedContracts

        if (statusFilter !== 'all') {
            filtered = filtered.filter(
                (contract) => contract.lifecycle_status === statusFilter,
            )
        }

        if (catalogTypeFilter === 'untyped') {
            filtered = filtered.filter((contract) =>
                isCertificates
                    ? !contract.organization_certificate_type
                    : !contract.organization_contract_type,
            )
        } else if (catalogTypeFilter !== 'all') {
            filtered = filtered.filter((contract) =>
                isCertificates
                    ? contract.organization_certificate_type ===
                      catalogTypeFilter
                    : contract.organization_contract_type === catalogTypeFilter,
            )
        }

        if (propertyFilter !== 'all') {
            filtered = filtered.filter((contract) =>
                (contract.apartment_ids ?? contract.apartments ?? []).includes(
                    propertyFilter,
                ),
            )
        }

        if (vendorFilter === 'unassigned') {
            filtered = filtered.filter((contract) => !contract.vendor?.id)
        } else if (vendorFilter !== 'all') {
            filtered = filtered.filter(
                (contract) => contract.vendor?.id === vendorFilter,
            )
        }

        if (autoRenewFilter !== 'all') {
            filtered = filtered.filter((contract) =>
                autoRenewFilter === 'yes'
                    ? contract.auto_renew
                    : !contract.auto_renew,
            )
        }

        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase()
            filtered = filtered.filter((contract) => {
                const catalogName = isCertificates
                    ? contract.organization_certificate_type_detail?.name
                    : contract.organization_contract_type_detail?.name
                return (
                    contract.name.toLowerCase().includes(searchLower) ||
                    (contract.ai_summary || '')
                        .toLowerCase()
                        .includes(searchLower) ||
                    (contract.apartment_names ?? [])
                        .join(' ')
                        .toLowerCase()
                        .includes(searchLower) ||
                    (contract.vendor?.name || '')
                        .toLowerCase()
                        .includes(searchLower) ||
                    (catalogName || '').toLowerCase().includes(searchLower)
                )
            })
        }

        return filtered
    }, [
        scopedContracts,
        searchText,
        statusFilter,
        catalogTypeFilter,
        propertyFilter,
        vendorFilter,
        autoRenewFilter,
        isCertificates,
    ])

    const hasActiveLibraryFilters =
        searchText.trim() !== '' ||
        statusFilter !== 'all' ||
        catalogTypeFilter !== 'all' ||
        propertyFilter !== 'all' ||
        vendorFilter !== 'all' ||
        autoRenewFilter !== 'all' ||
        selectedTagId != null

    const clearDrawerFilters = () => {
        setStatusFilter('all')
        setCatalogTypeFilter('all')
        setPropertyFilter('all')
        setVendorFilter('all')
        setAutoRenewFilter('all')
        onSelectedTagIdChange(null)
        setPage(0)
    }

    const libraryFilterChips = useMemo<FilterChipItem[]>(() => {
        const chips: FilterChipItem[] = []
        if (catalogTypeFilter === 'untyped') {
            chips.push({
                id: 'type',
                label: 'Type: Untyped',
                onDelete: () => setCatalogTypeFilter('all'),
            })
        } else if (catalogTypeFilter !== 'all') {
            const typeName =
                catalogTypes.find((type) => type.id === catalogTypeFilter)
                    ?.name || 'Type'
            chips.push({
                id: 'type',
                label: `Type: ${typeName}`,
                onDelete: () => setCatalogTypeFilter('all'),
            })
        }
        if (statusFilter !== 'all') {
            const labels: Record<ContractLifecycleStatus, string> = {
                active: 'Active',
                ending_soon: 'Ending soon',
                auto_renew_notice: 'Auto-renew notice',
                expired: 'Expired',
                no_dates: 'No dates',
            }
            chips.push({
                id: 'status',
                label: `Status: ${labels[statusFilter]}`,
                onDelete: () => setStatusFilter('all'),
            })
        }
        if (propertyFilter !== 'all') {
            const propertyName =
                propertyList.find((property) => property.id === propertyFilter)
                    ?.name || 'Property'
            chips.push({
                id: 'property',
                label: `Property: ${propertyName}`,
                onDelete: () => setPropertyFilter('all'),
            })
        }
        if (vendorFilter === 'unassigned') {
            chips.push({
                id: 'vendor',
                label: 'Vendor: Unassigned',
                onDelete: () => setVendorFilter('all'),
            })
        } else if (vendorFilter !== 'all') {
            const vendorName =
                vendorOptions.find((vendor) => vendor.id === vendorFilter)
                    ?.name || 'Vendor'
            chips.push({
                id: 'vendor',
                label: `Vendor: ${vendorName}`,
                onDelete: () => setVendorFilter('all'),
            })
        }
        const tagChip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange(null),
        )
        if (tagChip) chips.push(tagChip)
        if (autoRenewFilter !== 'all') {
            chips.push({
                id: 'autorenew',
                label:
                    autoRenewFilter === 'yes'
                        ? 'Auto-renew: Yes'
                        : 'Auto-renew: No',
                onDelete: () => setAutoRenewFilter('all'),
            })
        }
        return chips
    }, [
        catalogTypeFilter,
        catalogTypes,
        statusFilter,
        propertyFilter,
        propertyList,
        vendorFilter,
        vendorOptions,
        autoRenewFilter,
        selectedTagId,
        userTags,
        onSelectedTagIdChange,
    ])

    const visibleAlerts = useMemo(() => {
        const categoryAlerts = alerts.filter((alert) => {
            const type =
                alert.contract.contract_type ??
                ApartmentContractType.GENERAL_VENDOR
            return isCertificates
                ? type === ApartmentContractType.CERTIFICATE
                : type === contractTypeFilter
        })
        if (selectedTagId == null) return categoryAlerts
        const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
        if (!selectedTag) return categoryAlerts
        return categoryAlerts.filter((alert) =>
            (
                alert.contract.apartment_ids ??
                alert.contract.apartments ??
                []
            ).some((id) => selectedTag.apartments.includes(id)),
        )
    }, [alerts, selectedTagId, userTags, isCertificates, contractTypeFilter])

    const selectedCoveragePropertyIds = useMemo(() => {
        if (selectedTagId == null) return null
        const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
        return selectedTag?.apartments ?? []
    }, [selectedTagId, userTags])

    // Paginate filtered contracts
    const paginatedContracts = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredContracts.slice(start, end)
    }, [filteredContracts, page, rowsPerPage])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleView = (contract: ApartmentContract) => {
        setSelectedContract(contract)
        setViewStartsEditing(false)
        setViewModalOpen(true)
    }

    const handleCloseViewModal = () => {
        setViewModalOpen(false)
        setSelectedContract(null)
    }

    const handleReanalyze = (id: number) => {
        processContract.mutate(id)
    }

    const handleShare = async (contract: ApartmentContract) => {
        if (!contract.file) {
            toast.error('No contract link available')
            return
        }
        try {
            await navigator.clipboard.writeText(contract.file)
            toast.success('Contract link copied to clipboard.')
        } catch (error) {
            const textArea = window.document.createElement('textarea')
            textArea.value = contract.file
            window.document.body.appendChild(textArea)
            textArea.select()
            window.document.execCommand('copy')
            window.document.body.removeChild(textArea)
            toast.success('Contract link copied to clipboard.')
        }
    }

    const handleDeleteClick = (contract: ApartmentContract) => {
        setContractToDelete(contract)
        setDeleteDialogOpen(true)
    }

    const handleConfirmDelete = () => {
        if (!contractToDelete) return
        deleteContract.mutate(contractToDelete.id, {
            onSuccess: () => {
                setDeleteDialogOpen(false)
                setContractToDelete(null)
            },
        })
    }

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false)
        setContractToDelete(null)
    }

    const handleEdit = (contract: ApartmentContract) => {
        setSelectedContract(contract)
        setViewStartsEditing(true)
        setViewModalOpen(true)
    }

    const handleSaveContract = (
        contractId: number,
        data: updateContractRequest,
    ) => updateContract.mutateAsync({ contractId, data })

    const handleFilesDropped = (files: File[]) => {
        if (files.length > 0) {
            setPendingFiles(files)
            setPendingContractType(
                isCertificates
                    ? ApartmentContractType.CERTIFICATE
                    : contractTypeFilter,
            )
            setUploadDialogOpen(true)
        }
    }

    const handleUploadConfirm = async (apartmentIds: number[]) => {
        const catalogTypeId =
            pendingOrganizationContractTypeId === ''
                ? null
                : pendingOrganizationContractTypeId
        const results = await uploadContracts(
            pendingFiles,
            apartmentIds,
            pendingContractType,
            isCertificates ? null : catalogTypeId,
            isCertificates ? catalogTypeId : null,
        )
        results.forEach((result) => {
            if (result.status === 'fulfilled' && result.value?.data?.id) {
                processContract.mutate(result.value.data.id)
            }
        })
        setPendingFiles([])
        setPendingContractType(contractTypeFilter)
        setUploadDialogOpen(false)
    }

    if (isLoadingContracts) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <CatalogWorkspaceHeader
                title={isCertificates ? 'Certificates' : 'Contracts'}
                librarySubtitle={
                    isCertificates
                        ? 'Search, filter, and upload certificates across the portfolio.'
                        : 'Search, filter, and upload vendor contracts across the portfolio.'
                }
                coverageSubtitle={
                    isCertificates
                        ? 'See which properties have the certificate types they are required to keep on file.'
                        : 'See which properties have the contract types they are required to keep on file.'
                }
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                typesLabel={
                    isCertificates ? 'Certificate types' : 'Contract types'
                }
                onOpenTypes={() => setContractTypesOpen(true)}
                typesDisabled={!organization}
                coverageAlertCount={coverageAlertCount}
            />

            {viewMode === 'coverage' ? (
                <OrganizationContractTypeCoverage
                    coverage={typeCoverage}
                    isLoading={isLoadingCoverage}
                    selectedPropertyIds={selectedCoveragePropertyIds}
                    itemNoun={isCertificates ? 'certificate' : 'contract'}
                    selectedTagId={selectedTagId}
                    onSelectedTagIdChange={onSelectedTagIdChange}
                />
            ) : (
                <>
                    {/* Analytics Cards */}
                    <Box className={classes.analyticsContainer}>
                        <Paper
                            className={`${classes.analyticsCard} ${classes.totalCard}`}
                            elevation={1}
                        >
                            <Box
                                className={`${classes.analyticsIconContainer} ${classes.totalIconContainer}`}
                            >
                                <DescriptionIcon
                                    className={`${classes.analyticsIcon} ${classes.totalIcon}`}
                                />
                            </Box>
                            <Box className={classes.analyticsContent}>
                                <Typography className={classes.analyticsValue}>
                                    {scopedAnalytics.total}
                                </Typography>
                                <Typography className={classes.analyticsLabel}>
                                    {isCertificates
                                        ? 'Total Certificates'
                                        : 'Total Contracts'}
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper
                            className={`${classes.analyticsCard} ${
                                classes.activeCard
                            } ${classes.clickableCard} ${
                                statusFilter === 'active'
                                    ? classes.selectedCard
                                    : ''
                            }`}
                            elevation={1}
                            onClick={() =>
                                setStatusFilter(
                                    statusFilter === 'active'
                                        ? 'all'
                                        : 'active',
                                )
                            }
                        >
                            <Box
                                className={`${classes.analyticsIconContainer} ${classes.activeIconContainer}`}
                            >
                                <CheckCircleIcon
                                    className={`${classes.analyticsIcon} ${classes.activeIcon}`}
                                />
                            </Box>
                            <Box className={classes.analyticsContent}>
                                <Typography className={classes.analyticsValue}>
                                    {scopedAnalytics.active}
                                </Typography>
                                <Typography className={classes.analyticsLabel}>
                                    Active
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper
                            className={`${classes.analyticsCard} ${
                                classes.endingSoonCard
                            } ${classes.clickableCard} ${
                                statusFilter === 'ending_soon'
                                    ? classes.selectedCard
                                    : ''
                            }`}
                            elevation={1}
                            onClick={() =>
                                setStatusFilter(
                                    statusFilter === 'ending_soon'
                                        ? 'all'
                                        : 'ending_soon',
                                )
                            }
                        >
                            <Box
                                className={`${classes.analyticsIconContainer} ${classes.endingSoonIconContainer}`}
                            >
                                <WarningIcon
                                    className={`${classes.analyticsIcon} ${classes.endingSoonIcon}`}
                                />
                            </Box>
                            <Box className={classes.analyticsContent}>
                                <Typography className={classes.analyticsValue}>
                                    {scopedAnalytics.ending_soon}
                                </Typography>
                                <Typography className={classes.analyticsLabel}>
                                    Ending Soon
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper
                            className={`${classes.analyticsCard} ${
                                classes.autoRenewCard
                            } ${classes.clickableCard} ${
                                statusFilter === 'auto_renew_notice'
                                    ? classes.selectedCard
                                    : ''
                            }`}
                            elevation={1}
                            onClick={() =>
                                setStatusFilter(
                                    statusFilter === 'auto_renew_notice'
                                        ? 'all'
                                        : 'auto_renew_notice',
                                )
                            }
                        >
                            <Box
                                className={`${classes.analyticsIconContainer} ${classes.autoRenewIconContainer}`}
                            >
                                <AutorenewIcon
                                    className={`${classes.analyticsIcon} ${classes.autoRenewIcon}`}
                                />
                            </Box>
                            <Box className={classes.analyticsContent}>
                                <Typography className={classes.analyticsValue}>
                                    {scopedAnalytics.auto_renew_notice ?? 0}
                                </Typography>
                                <Typography className={classes.analyticsLabel}>
                                    Auto-Renew
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper
                            className={`${classes.analyticsCard} ${
                                classes.expiredCard
                            } ${classes.clickableCard} ${
                                statusFilter === 'expired'
                                    ? classes.selectedCard
                                    : ''
                            }`}
                            elevation={1}
                            onClick={() =>
                                setStatusFilter(
                                    statusFilter === 'expired'
                                        ? 'all'
                                        : 'expired',
                                )
                            }
                        >
                            <Box
                                className={`${classes.analyticsIconContainer} ${classes.expiredIconContainer}`}
                            >
                                <ErrorIcon
                                    className={`${classes.analyticsIcon} ${classes.expiredIcon}`}
                                />
                            </Box>
                            <Box className={classes.analyticsContent}>
                                <Typography className={classes.analyticsValue}>
                                    {scopedAnalytics.expired}
                                </Typography>
                                <Typography className={classes.analyticsLabel}>
                                    Expired
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper
                            className={`${classes.analyticsCard} ${
                                classes.noDatesCard
                            } ${classes.clickableCard} ${
                                statusFilter === 'no_dates'
                                    ? classes.selectedCard
                                    : ''
                            }`}
                            elevation={1}
                            onClick={() =>
                                setStatusFilter(
                                    statusFilter === 'no_dates'
                                        ? 'all'
                                        : 'no_dates',
                                )
                            }
                        >
                            <Box
                                className={`${classes.analyticsIconContainer} ${classes.noDatesIconContainer}`}
                            >
                                <HelpOutlineIcon
                                    className={`${classes.analyticsIcon} ${classes.noDatesIcon}`}
                                />
                            </Box>
                            <Box className={classes.analyticsContent}>
                                <Typography className={classes.analyticsValue}>
                                    {scopedAnalytics.no_dates}
                                </Typography>
                                <Typography className={classes.analyticsLabel}>
                                    No Dates
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>

                    <ContractAlertsPanel
                        alerts={visibleAlerts}
                        itemNoun={isCertificates ? 'certificate' : 'contract'}
                        storageKey={
                            isCertificates
                                ? 'ezfolio-certificate-alerts-closed'
                                : 'ezfolio-contract-alerts-closed'
                        }
                        onSelect={(contract) => {
                            setSelectedContract(contract)
                            setViewModalOpen(true)
                        }}
                        onOpenEmailSettings={() => setEmailSettingsOpen(true)}
                    />
                    <ContractAlertEmailSettingsDialog
                        open={emailSettingsOpen}
                        onClose={() => setEmailSettingsOpen(false)}
                        organization={organization}
                        scopeLabel="Organization default. Property settings can override this."
                    />
                    {!isCertificates && (
                        <Tabs
                            value={contractTypeFilter}
                            onChange={(
                                _event,
                                value: ApartmentContractType,
                            ) => {
                                setContractTypeFilter(value)
                                setPendingContractType(value)
                                setPage(0)
                            }}
                            indicatorColor="primary"
                            textColor="primary"
                        >
                            {CONTRACT_CATEGORIES.map((category) => (
                                <Tab
                                    key={category.value}
                                    value={category.value}
                                    label={category.label}
                                    style={{
                                        textTransform: 'none',
                                        fontWeight: 500,
                                    }}
                                />
                            ))}
                        </Tabs>
                    )}

                    <DropFiles
                        onDrop={handleFilesDropped}
                        displayString={
                            isUploading
                                ? 'Uploading...'
                                : isCertificates
                                ? 'Drag and drop certificate files here (PDF, DOCX, TXT)'
                                : 'Drag and drop contract files here (PDF, DOCX, TXT)'
                        }
                        fileTypes={allowedFileTypes}
                        multipleFiles={true}
                    />

                    <CatalogFilterBar
                        searchPlaceholder={
                            isCertificates
                                ? 'Search certificates...'
                                : 'Search contracts...'
                        }
                        searchValue={searchText}
                        onSearchChange={(value) => {
                            setSearchText(value)
                            setPage(0)
                        }}
                        onOpenFilters={() => setFilterDrawerOpen(true)}
                        chips={libraryFilterChips}
                        onClearAll={
                            libraryFilterChips.length > 0
                                ? clearDrawerFilters
                                : undefined
                        }
                        resultLabel={`${filteredContracts.length} ${
                            isCertificates ? 'certificate' : 'contract'
                        }${filteredContracts.length !== 1 ? 's' : ''}`}
                    />
                    <CatalogFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        title={
                            isCertificates
                                ? 'Certificate filters'
                                : 'Contract filters'
                        }
                        activeCount={libraryFilterChips.length}
                        onClear={clearDrawerFilters}
                    >
                        <PropertyTagFilterField
                            value={selectedTagId}
                            onChange={(value) => {
                                onSelectedTagIdChange(value)
                                setPage(0)
                            }}
                        />
                        <TextField
                            label={
                                isCertificates
                                    ? 'Certificate type'
                                    : 'Contract type'
                            }
                            variant="outlined"
                            size="small"
                            select
                            fullWidth
                            value={catalogTypeFilter}
                            onChange={(e) => {
                                const value = e.target.value
                                setCatalogTypeFilter(
                                    value === 'all' || value === 'untyped'
                                        ? value
                                        : Number(value),
                                )
                                setPage(0)
                            }}
                        >
                            <MenuItem value="all">All types</MenuItem>
                            <MenuItem value="untyped">Untyped</MenuItem>
                            {catalogTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Status"
                            variant="outlined"
                            size="small"
                            select
                            fullWidth
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(
                                    e.target.value as
                                        | ContractLifecycleStatus
                                        | 'all',
                                )
                                setPage(0)
                            }}
                        >
                            <MenuItem value="all">All statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="ending_soon">Ending soon</MenuItem>
                            <MenuItem value="auto_renew_notice">
                                Auto-renew notice
                            </MenuItem>
                            <MenuItem value="expired">Expired</MenuItem>
                            <MenuItem value="no_dates">No dates</MenuItem>
                        </TextField>
                        <TextField
                            label="Property"
                            variant="outlined"
                            size="small"
                            select
                            fullWidth
                            value={propertyFilter}
                            onChange={(e) => {
                                const value = e.target.value
                                setPropertyFilter(
                                    value === 'all' ? 'all' : Number(value),
                                )
                                setPage(0)
                            }}
                        >
                            <MenuItem value="all">All properties</MenuItem>
                            {propertyList.map((property) => (
                                <MenuItem key={property.id} value={property.id}>
                                    {property.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Vendor"
                            variant="outlined"
                            size="small"
                            select
                            fullWidth
                            value={vendorFilter}
                            onChange={(e) => {
                                const value = e.target.value
                                setVendorFilter(
                                    value === 'all' || value === 'unassigned'
                                        ? value
                                        : Number(value),
                                )
                                setPage(0)
                            }}
                        >
                            <MenuItem value="all">All vendors</MenuItem>
                            <MenuItem value="unassigned">Unassigned</MenuItem>
                            {vendorOptions.map((vendor) => (
                                <MenuItem key={vendor.id} value={vendor.id}>
                                    {vendor.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Auto-renew"
                            variant="outlined"
                            size="small"
                            select
                            fullWidth
                            value={autoRenewFilter}
                            onChange={(e) => {
                                setAutoRenewFilter(
                                    e.target.value as 'all' | 'yes' | 'no',
                                )
                                setPage(0)
                            }}
                        >
                            <MenuItem value="all">Any</MenuItem>
                            <MenuItem value="yes">Auto-renew</MenuItem>
                            <MenuItem value="no">No auto-renew</MenuItem>
                        </TextField>
                    </CatalogFilterDrawer>

                    {/* Table */}
                    <Paper className={classes.tableWrapper}>
                        <TableContainer className={classes.tableContainer}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow className={classes.headerRow}>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Name
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Type
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Status
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Properties
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Vendor
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Start Date
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            End Date
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            AI Summary
                                        </TableCell>
                                        <TableCell
                                            className={`${classes.headerCell} ${classes.actionsCell}`}
                                            align="right"
                                        >
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedContracts.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={9}
                                                className={classes.emptyRow}
                                            >
                                                <Typography
                                                    className={
                                                        classes.emptyTitle
                                                    }
                                                >
                                                    {hasActiveLibraryFilters
                                                        ? isCertificates
                                                            ? 'No matching certificates'
                                                            : 'No matching contracts'
                                                        : isCertificates
                                                        ? 'No certificates yet'
                                                        : 'No contracts in this category'}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    {hasActiveLibraryFilters
                                                        ? 'Try a different search or clear the filters.'
                                                        : isCertificates
                                                        ? 'Drop a file above to add the first certificate.'
                                                        : 'Drop a file above to add the first contract.'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedContracts.map((contract) => (
                                            <TableRow
                                                key={contract.id}
                                                className={lifecycleRowClassName(
                                                    classes,
                                                    contract.lifecycle_status,
                                                )}
                                                onClick={() =>
                                                    handleView(contract)
                                                }
                                            >
                                                <TableCell
                                                    className={classes.nameCell}
                                                >
                                                    {contract.name}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {(contract.contract_type ===
                                                        ApartmentContractType.CERTIFICATE
                                                            ? contract.organization_certificate_type_detail
                                                            : contract.organization_contract_type_detail
                                                        )?.name || 'Untyped'}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {formatContractType(
                                                            contract.contract_type,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <ContractStatusChip
                                                        status={
                                                            contract.lifecycle_status
                                                        }
                                                        autoRenew={
                                                            contract.auto_renew
                                                        }
                                                        endDate={
                                                            contract.end_date
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        classes.propertyCell
                                                    }
                                                >
                                                    <Box
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection:
                                                                'row',
                                                            alignItems:
                                                                'center',
                                                            flexWrap: 'wrap',
                                                            gap: 4,
                                                        }}
                                                    >
                                                        {(() => {
                                                            const names =
                                                                contract.apartment_names ??
                                                                []
                                                            const apartmentIds =
                                                                contract.apartment_ids ??
                                                                contract.apartments ??
                                                                []
                                                            const singleProperty =
                                                                apartmentIds.length ===
                                                                1
                                                            const tags =
                                                                singleProperty &&
                                                                apartmentIds.length >
                                                                    0
                                                                    ? getTagsForProperty(
                                                                          apartmentIds[0],
                                                                      )
                                                                    : []
                                                            const maxVisible = 2
                                                            const visibleText =
                                                                names.length <=
                                                                maxVisible
                                                                    ? names.join(
                                                                          ', ',
                                                                      ) || '—'
                                                                    : names
                                                                          .slice(
                                                                              0,
                                                                              maxVisible,
                                                                          )
                                                                          .join(
                                                                              ', ',
                                                                          )
                                                            const restCount =
                                                                names.length -
                                                                maxVisible
                                                            return (
                                                                <>
                                                                    {names.length >
                                                                    maxVisible ? (
                                                                        <Tooltip
                                                                            title={names.join(
                                                                                ', ',
                                                                            )}
                                                                            placement="top"
                                                                            enterDelay={
                                                                                200
                                                                            }
                                                                        >
                                                                            <span
                                                                                style={{
                                                                                    cursor:
                                                                                        'default',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    visibleText
                                                                                }
                                                                                <Typography
                                                                                    component="span"
                                                                                    color="primary"
                                                                                    className={
                                                                                        classes.propertyNamesMore
                                                                                    }
                                                                                >
                                                                                    +
                                                                                    {
                                                                                        restCount
                                                                                    }{' '}
                                                                                    more
                                                                                </Typography>
                                                                            </span>
                                                                        </Tooltip>
                                                                    ) : (
                                                                        <span>
                                                                            {
                                                                                visibleText
                                                                            }
                                                                        </span>
                                                                    )}
                                                                    {tags.length >
                                                                        0 && (
                                                                        <Box
                                                                            display="flex"
                                                                            flexWrap="wrap"
                                                                            style={{
                                                                                gap: 4,
                                                                            }}
                                                                        >
                                                                            {tags.map(
                                                                                (
                                                                                    tag,
                                                                                ) => (
                                                                                    <Chip
                                                                                        key={
                                                                                            tag.id
                                                                                        }
                                                                                        label={
                                                                                            tag.name
                                                                                        }
                                                                                        size="small"
                                                                                        className={
                                                                                            classes.tagChip
                                                                                        }
                                                                                        style={{
                                                                                            backgroundColor:
                                                                                                tag.color,
                                                                                            color:
                                                                                                'white',
                                                                                        }}
                                                                                    />
                                                                                ),
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </>
                                                            )
                                                        })()}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {contract.vendor?.name ||
                                                        '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(
                                                        contract.start_date,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(
                                                        contract.end_date,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {processingIds.has(
                                                        contract.id,
                                                    ) ? (
                                                        <Box
                                                            className={
                                                                classes.analyzingContainer
                                                            }
                                                        >
                                                            <CircularProgress
                                                                size={14}
                                                                color="primary"
                                                            />
                                                            <Typography
                                                                className={
                                                                    classes.analyzingText
                                                                }
                                                            >
                                                                Analyzing...
                                                            </Typography>
                                                        </Box>
                                                    ) : contract.ai_summary ? (
                                                        <Tooltip
                                                            title={
                                                                <Typography
                                                                    variant="body2"
                                                                    style={{
                                                                        whiteSpace:
                                                                            'pre-wrap',
                                                                    }}
                                                                >
                                                                    {
                                                                        contract.ai_summary
                                                                    }
                                                                </Typography>
                                                            }
                                                            arrow
                                                            placement="bottom-start"
                                                            interactive
                                                        >
                                                            <Box
                                                                className={
                                                                    classes.summaryCell
                                                                }
                                                            >
                                                                {
                                                                    contract.ai_summary
                                                                }
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        classes.actionsCell
                                                    }
                                                    onClick={(event) =>
                                                        event.stopPropagation()
                                                    }
                                                >
                                                    <Tooltip title="Share Link">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleShare(
                                                                    contract,
                                                                )
                                                            }
                                                        >
                                                            <ShareIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="View">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleView(
                                                                    contract,
                                                                )
                                                            }
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleEdit(
                                                                    contract,
                                                                )
                                                            }
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reanalyze">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleReanalyze(
                                                                        contract.id,
                                                                    )
                                                                }
                                                                disabled={processingIds.has(
                                                                    contract.id,
                                                                )}
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                handleDeleteClick(
                                                                    contract,
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            count={filteredContracts.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </Paper>
                </>
            )}

            {/* View Contract Modal */}
            <ContractViewModal
                isOpen={viewModalOpen}
                onClose={handleCloseViewModal}
                contract={viewedContract}
                initialEditing={viewStartsEditing}
                vendorList={orgVendorList}
                apartmentList={propertyList}
                onSave={handleSaveContract}
                isSaving={isUpdating}
            />

            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                documentName={contractToDelete?.name || ''}
                isDeleting={isDeleting}
            />

            <SelectPropertiesUploadDialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false)
                    setPendingFiles([])
                    setPendingContractType(contractTypeFilter)
                }}
                files={pendingFiles}
                propertyList={propertyList}
                title={
                    isCertificates
                        ? 'Select properties for these certificates'
                        : 'Select properties for these contracts'
                }
                submitLabel="Upload"
                onConfirm={handleUploadConfirm}
                isSubmitting={isUploading}
            >
                <Box className={classes.uploadSection}>
                    <TextField
                        select
                        label={
                            isCertificates
                                ? 'Certificate type'
                                : 'Contract type'
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={pendingOrganizationContractTypeId}
                        onChange={(e) =>
                            setPendingOrganizationContractTypeId(
                                e.target.value === ''
                                    ? ''
                                    : Number(e.target.value),
                            )
                        }
                        style={{ marginBottom: 16 }}
                    >
                        <MenuItem value="">Untyped</MenuItem>
                        {catalogTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                                {type.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Typography variant="body2" color="textSecondary">
                        These files will be saved under{' '}
                        {
                            CONTRACT_TYPE_LABELS[
                                isCertificates
                                    ? ApartmentContractType.CERTIFICATE
                                    : contractTypeFilter
                            ]
                        }
                        .
                    </Typography>
                </Box>
            </SelectPropertiesUploadDialog>

            {organization != null && (
                <OrganizationContractTypesModal
                    open={contractTypesOpen}
                    onClose={() => setContractTypesOpen(false)}
                    organizationId={organization}
                    propertyList={propertyList}
                    catalog={isCertificates ? 'certificate' : 'contract'}
                />
            )}
        </Box>
    )
}
